import React from "react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ArrowRight, Gem, Layers, Grid3x3, Mountain } from "lucide-react";
const materials = [{
  title: "Marble",
  description: "Timeless elegance with unique natural veining. Offers unmatched sophistication and a cool, premium feel for luxury bathroom suites.",
  benefit: "Luxury & Status",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/marble_texture_detail.png",
  icon: Gem
}, {
  title: "Porcelain",
  description: "Engineered for extreme durability and water resistance. The perfect choice for modern wet rooms requiring a sleek, consistent finish.",
  benefit: "Durability & Hygiene",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/porcelain_tile_surface.png",
  icon: Layers
}, {
  title: "Mosaic",
  description: "Intricate patterns that add artistic flair. Ideal for shower floors, niches, and feature walls to create visual depth and texture.",
  benefit: "Detail & Grip",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/mosaic_pattern_blue.png",
  icon: Grid3x3
}, {
  title: "Natural Stone",
  description: "Organic textures that bring the outdoors in. Provides a warm, spa-like atmosphere with rugged, earthy tones and tactile surfaces.",
  benefit: "Organic Warmth",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/natural_stone_wall.png",
  icon: Mountain
}];
export default function MaterialShowcase() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="material-showcase" className="py-20 relative overflow-hidden !text-[hsl(222,47%,11%)] !shadow-[0px_2px_4px_rgba(20,21,31,0.15),_0px_1px_2px_rgba(20,21,31,0.10)] !bg-[hsl(222,47%,11%)]">
      <style dangerouslySetInnerHTML={{
      __html: `
        :root {
          --background: hsl(0 0% 100%);
          --foreground: hsl(0 0% 95%);
          --card-foreground: hsl(0 0% 95%);
          --popover-foreground: hsl(0 0% 95%);
          --primary-foreground: hsl(0 0% 95%);
          --secondary-foreground: hsl(0 0% 95%);
          --muted-foreground: hsl(0 0% 95%);
          --sidebar-foreground: hsl(0 0% 95%);
        }
        .dark {
          --foreground: hsl(0 0% 95%);
          --card-foreground: hsl(0 0% 95%);
          --popover-foreground: hsl(0 0% 95%);
          --primary-foreground: hsl(0 0% 95%);
          --secondary-foreground: hsl(0 0% 95%);
          --muted-foreground: hsl(0 0% 95%);
          --sidebar-foreground: hsl(0 0% 95%);
        }
        body { text-align: left; }
        .container { margin-left: 0; margin-right: auto; }
        .flex, .grid { justify-content: flex-start; }
      `
    }} />
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif font-medium text-foreground mb-6 tracking-tight">
            Curated Material Palette
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            We source only the finest materials to ensure your renovation stands the test of time. 
            Explore our selection of premium finishes designed for both beauty and performance.
          </p>
        </div>

        <div ref={ref} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {materials.map((item, i) => <div key={i} data-index={i} className="group flex flex-col items-center text-center">
              {}
              <div className="relative w-48 h-48 md:w-56 md:h-56 rounded-full overflow-hidden border-4 border-border shadow-lg mb-8 bg-muted">
                <div className="absolute inset-0 bg-black/10 z-10 group-hover:bg-transparent transition-colors duration-500" />
                <img src={item.image} alt={`${item.title} texture sample`} className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110" />
              </div>

              {}
              <Card className="border-none shadow-none bg-transparent w-full">
                <CardHeader className="p-0 pb-4 items-center">
                  <div className="flex items-center gap-2 text-foreground font-medium text-sm uppercase tracking-wider mb-2">
                    <item.icon className="w-4 h-4" />
                    <span>{item.benefit}</span>
                  </div>
                  <CardTitle className="text-2xl font-serif text-card-foreground">
                    {item.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <CardDescription className="text-base text-muted-foreground leading-relaxed">
                    {item.description}
                  </CardDescription>
                </CardContent>
              </Card>
            </div>)}
        </div>

        <div className="mt-16 text-center">
          <Button asChild size="lg" className="rounded-full px-8">
            <Link to="#">
              View Projects Gallery <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}